import { useEffect, useMemo, useRef, useState } from "react";

const ROOT_EXIT_GUARD_KEY = "__rootExitGuard";

function getIsAndroidRootExitContext() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent || "";
  const isAndroid = /Android/i.test(userAgent);

  if (!isAndroid) {
    return false;
  }

  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true;
  const isMobileViewport = window.innerWidth <= 768;

  return isStandalone || isMobileViewport;
}

function buildGuardState(currentState) {
  return {
    ...(currentState || {}),
    [ROOT_EXIT_GUARD_KEY]: true,
  };
}

export default function useAndroidRootExitGuard({
  isAtTrueRoot,
  timeoutMs = 1800,
}) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isSupportedContext, setIsSupportedContext] = useState(
    getIsAndroidRootExitContext
  );
  const isAtTrueRootRef = useRef(isAtTrueRoot);
  const promptDeadlineRef = useRef(0);
  const promptTimerRef = useRef(null);
  const bypassNextPopRef = useRef(false);

  const enabled = useMemo(
    () => isSupportedContext && isAtTrueRoot,
    [isAtTrueRoot, isSupportedContext]
  );

  useEffect(() => {
    isAtTrueRootRef.current = isAtTrueRoot;
  }, [isAtTrueRoot]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia
      ? window.matchMedia("(display-mode: standalone)")
      : null;

    const updateContext = () => {
      setIsSupportedContext(getIsAndroidRootExitContext());
    };

    updateContext();
    window.addEventListener("resize", updateContext);
    mediaQuery?.addEventListener?.("change", updateContext);

    return () => {
      window.removeEventListener("resize", updateContext);
      mediaQuery?.removeEventListener?.("change", updateContext);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (promptTimerRef.current) {
        window.clearTimeout(promptTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (enabled) {
      if (!window.history.state?.[ROOT_EXIT_GUARD_KEY]) {
        window.history.pushState(
          buildGuardState(window.history.state),
          "",
          window.location.href
        );
      }

      return;
    }

    promptDeadlineRef.current = 0;
    setShowPrompt(false);

    if (promptTimerRef.current) {
      window.clearTimeout(promptTimerRef.current);
      promptTimerRef.current = null;
    }
  }, [enabled]);

  useEffect(() => {
    if (!isSupportedContext || typeof window === "undefined") {
      return undefined;
    }

    const handlePopState = () => {
      if (bypassNextPopRef.current) {
        bypassNextPopRef.current = false;
        return;
      }

      if (window.history.state?.[ROOT_EXIT_GUARD_KEY]) {
        return;
      }

      if (!isAtTrueRootRef.current) {
        return;
      }

      const now = Date.now();
      const isSecondBackWithinWindow = promptDeadlineRef.current > now;

      if (isSecondBackWithinWindow) {
        promptDeadlineRef.current = 0;
        setShowPrompt(false);

        if (promptTimerRef.current) {
          window.clearTimeout(promptTimerRef.current);
          promptTimerRef.current = null;
        }

        bypassNextPopRef.current = true;
        window.setTimeout(() => {
          window.history.back();
        }, 0);
        return;
      }

      promptDeadlineRef.current = now + timeoutMs;
      setShowPrompt(true);

      if (promptTimerRef.current) {
        window.clearTimeout(promptTimerRef.current);
      }

      promptTimerRef.current = window.setTimeout(() => {
        promptDeadlineRef.current = 0;
        setShowPrompt(false);
        promptTimerRef.current = null;
      }, timeoutMs);

      window.history.pushState(
        buildGuardState(window.history.state),
        "",
        window.location.href
      );
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isSupportedContext, timeoutMs]);

  return {
    isSupportedContext,
    showPrompt,
  };
}
